import { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { ElementPlacementUnion, ElementThemeUnion } from 'constants/routes';
import { ISelectOption } from 'interfaces';
import { useDebouncedCallback } from 'use-debounce';

import { Spinner } from 'components/ui/index';
import TextInput from 'components/ui/TextInput';
import { genericMemo } from 'utils';

import SelectItem from './SelectItem';

import './style.scss';

interface IOptionsListProps<T, P> {
  options: ISelectOption<T, P>[];
  enableFavorites?: boolean;
  value: T | T[];
  isLoading: boolean;
  withSearch: boolean;
  withEmpty: boolean;
  placeholder?: string;
  handleClick: (value: T, option: ISelectOption<T, P>) => void;
  isCreating?: boolean;
  isAddMode: boolean;
  creationText?: string;
  onCreate?: (value: string) => void;
  theme?: ElementThemeUnion;
  isExpandable: boolean;
  menuPlacement?: ElementPlacementUnion;
  className?: string;
  toggleFavorite?: (value: string, isFavorite: boolean) => void;
  isMultiple?: boolean;
}

const FIRST_LEVEL_DEPTH = 1;

const getFilteredOptions = (
  list: ISelectOption[],
  value: string
): ISelectOption[] => {
  if (value) {
    return list.filter((item: ISelectOption) => {
      if (item.children) {
        item.children = getFilteredOptions(item.children, value);

        if (item.children.length) {
          return true;
        }
      }

      return item.label.toLowerCase().includes(value.toLowerCase());
    });
  }

  return list;
};

const OptionsList = <T, P>({
  options,
  enableFavorites,
  value,
  withSearch,
  withEmpty,
  isLoading,
  placeholder = 'Поиск',
  handleClick,
  isCreating,
  isAddMode,
  creationText,
  onCreate,
  isExpandable,
  theme = 'light',
  menuPlacement = 'bottom',
  className,
  isMultiple,
  toggleFavorite,
}: IOptionsListProps<T, P>) => {
  const [searchValue, setSearchValue] = useState('');
  const [filteredOptions, setFilteredOptions] =
    useState<ISelectOption<T, P>[]>(options);
  const [isOpenCreation, setOpenCreation] = useState(false);
  const [newOption, setNewOption] = useState('');

  const pickFavorites = (options: ISelectOption[]) => {
    const favorites: ISelectOption[] = [];
    const selectFavorites = (option: ISelectOption) => {
      option.isFavorite && favorites.push({ ...option, children: [] }); // excluding children from favorite options
      option.children?.forEach(selectFavorites);
    };
    options.forEach(selectFavorites);
    return favorites;
  };

  const sortFlatOptions = (options: ISelectOption[]) =>
    options.sort((a, b) => {
      if (a.isFavorite && !b.isFavorite) return -1;
      if (!a.isFavorite && b.isFavorite) return 1;
      return 0;
    });

  const [favorites, sortedOptions] = useMemo(() => {
    if (isExpandable) return [pickFavorites(filteredOptions), filteredOptions];
    return [[], sortFlatOptions(filteredOptions)];
  }, [filteredOptions, options, isExpandable]);

  useEffect(() => {
    if (!enableFavorites) {
      document.getElementById(`option-${value}`)?.scrollIntoView({
        block: 'center',
      });
    }
  }, []);

  useEffect(() => {
    if (withSearch && searchValue) {
      getOptions(searchValue);
    } else {
      setFilteredOptions(options);
    }
  }, [options]);

  useEffect(() => {
    if (withSearch) {
      getOptions(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (!isCreating && newOption) {
      setNewOption('');
      setOpenCreation(false);
    }
  }, [isCreating]);

  const getOptions = useDebouncedCallback((search) => {
    let list = JSON.parse(JSON.stringify(options));

    if (search) {
      list = getFilteredOptions(list, search);
    }

    setFilteredOptions(list);
  }, 100);

  const handleEnter = () => {
    if (!isCreating && onCreate) {
      onCreate(newOption);
    }
  };

  const DEFAULT_TOP_HEIGHT = -15;
  const DEFAULT_BOTTOM_HEIGHT = 36;
  const OPTION_HEIGHT = 35;
  const VISIBLE_ITEMS = 10;
  const EXTRA_PADDING = 3; // magic number 3 is used to level out paddings

  const getMenuHeight = () => {
    const getHeightByOptionsLength = (length: number) => {
      if (length === 0) return DEFAULT_TOP_HEIGHT + 142;
      return (
        DEFAULT_TOP_HEIGHT +
        (VISIBLE_ITEMS - length) * OPTION_HEIGHT +
        EXTRA_PADDING
      );
    };

    if (menuPlacement === 'bottom') return DEFAULT_BOTTOM_HEIGHT;

    if (options.length <= VISIBLE_ITEMS) {
      return getHeightByOptionsLength(options.length);
    }

    if (filteredOptions.length <= VISIBLE_ITEMS)
      return getHeightByOptionsLength(filteredOptions.length);

    return DEFAULT_TOP_HEIGHT;
  };

  const renderSearchInput = () => (
    <li
      className={cn(
        'select__options__search [&_svg]:w-5 [&_svg]:h-5 border-tpg_light border-solid',
        menuPlacement === 'top' ? 'border-t' : 'border-b'
      )}
      key="select-option-search"
    >
      <TextInput
        searchIcon
        resetIcon
        placeholder={placeholder || 'Введите название'}
        onChange={setSearchValue}
        value={searchValue}
        size="m"
        theme={theme}
        rounded={false}
        classNames={{ input: 'tpg-c2 !pl-9 !text-tpg_light' }}
      />
    </li>
  );

  const renderSelectItem = (depth: number) => {
    const closure = (option: ISelectOption, index: number) => (
      <SelectItem
        key={`select-option-${option.label}-${index}`}
        value={value}
        option={option}
        enableFavorites={enableFavorites}
        searchValue={searchValue}
        depth={depth}
        handleClick={handleClick}
        isExpandable={isExpandable}
        toggleFavorite={toggleFavorite}
        idx={index}
        renderSubOption={renderSelectItem}
        isMultiple={isMultiple}
      />
    );
    return closure;
  };

  return (
    <ul
      className={cn(
        'select__options',
        {
          select__options__searching: withSearch,
          select__options__empty: !filteredOptions.length,
          'bg-ultrablack': theme === 'dark',
          'bg-light': theme === 'light',
        },
        className
      )}
      style={{
        top: `${getMenuHeight()}px`,
        maxHeight: `${OPTION_HEIGHT * VISIBLE_ITEMS}px`,
      }}
    >
      {withSearch && menuPlacement === 'bottom' && renderSearchInput()}
      {withEmpty && (
        <li className="select__options__item" key="select-option-empty">
          <span
            className={cn(
              'text-dark_product select__options__item__label pl-4 tpg-c2',
              {
                'select__options__item__label__selected ': !value,
              }
            )}
            onClick={() =>
              handleClick(null as T, { label: 'Не выбрано', value: null as T })
            }
          >
            Не выбрано
          </span>
        </li>
      )}
      {isAddMode && (
        <li
          className="select__options__item select__options__creation"
          key="select-option-creation"
        >
          {isOpenCreation ? (
            <TextInput
              resetIcon
              autoFocus
              placeholder="введите название"
              onChange={setNewOption}
              value={newOption}
              onEnter={handleEnter}
            />
          ) : (
            <button
              className="select__options__creation__btn"
              onClick={() => setOpenCreation(true)}
            >
              {creationText || '+ новый'}
            </button>
          )}
          {isCreating && <Spinner />}
        </li>
      )}
      {favorites.map(renderSelectItem(FIRST_LEVEL_DEPTH))}
      {sortedOptions.map(renderSelectItem(FIRST_LEVEL_DEPTH))}
      {!filteredOptions.length && (
        <div className="flex items-center justify-center text-tpg_base tpg-c2 pb-2 pt-2">
          {isLoading && <Spinner />}
          <span>{isLoading ? 'Загрузка...' : 'Нет данных'}</span>
        </div>
      )}

      <div className="sticky bottom-0 w-full">
        {withSearch && menuPlacement === 'top' && renderSearchInput()}
      </div>
    </ul>
  );
};

export default genericMemo(OptionsList);
